import { useEffect, createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { setTokenProvider, setAuthFailureCallback } from '@taxassist/api-client';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { configureApiClientEnvironment } from '../config/api-client-config';
import { useAuth } from '../contexts/AuthContext';

// Module-level flag to prevent double initialization in React StrictMode
let isTokenProviderInitialized = false;

interface ApiProviderProps {
  children: ReactNode;
}

interface ApiProviderContext {
  isTokenProviderReady: boolean;
}

const ApiProviderContext = createContext<ApiProviderContext>({
  isTokenProviderReady: false,
});

export const useApiProvider = () => useContext(ApiProviderContext);

export function ApiProvider({ children }: ApiProviderProps) {
  const [isTokenProviderReady, setIsTokenProviderReady] = useState(false);
  const { handleSessionExpired } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check module-level flag to prevent double initialization in React StrictMode
    if (isTokenProviderInitialized) {
      setIsTokenProviderReady(true);
      return;
    }

    isTokenProviderInitialized = true;

    // Configure API client environment variables
    configureApiClientEnvironment();

    // Configure the API client to use Cognito tokens
    const tokenProvider = {
      async getTokens() {
        try {
          const session = await fetchAuthSession();
          
          if (!session.tokens) {
            return null;
          }

          const tokens = {
            idToken: session.tokens.idToken?.toString() || '',
            accessToken: session.tokens.accessToken?.toString() || '',
          };

          // Debug: Log token payload to see what's being sent
          if (session.tokens.idToken) {
            const payload = session.tokens.idToken.payload;
          }

          return tokens;
        } catch (error) {
          console.error('❌ Failed to get Cognito tokens:', error);
          return null;
        }
      },
      async clearTokens() {
        try {
          // Sign out from Amplify to clear tokens and session
          await signOut({ global: true });
        } catch (error) {
          // Ignore errors during sign out - tokens might already be cleared
        }
      }
    };

    console.log('🔧 Setting up token provider...');
    setTokenProvider(tokenProvider);

    // Set up global auth failure handler
    setAuthFailureCallback(() => {
      console.warn('🔐 Session expired - redirecting to login');
      handleSessionExpired();
      navigate('/login', { replace: true });
    });

    setIsTokenProviderReady(true);
    console.log('✅ Token provider setup complete');
  }, [handleSessionExpired, navigate]);

  return (
    <ApiProviderContext.Provider value={{ isTokenProviderReady }}>
      {children}
    </ApiProviderContext.Provider>
  );
}